import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  Button,
  styled,
  CircularProgress,
  Select,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  SelectChangeEvent,
  Chip,
} from "@mui/material";
import { TransformedSection } from "@services/facilitytype";
import { getStates, StateData } from "@services/location";
// import removed: getRegulations not used
import { getSelectedCountryName, getSelectedCountryCode } from "@utils/countryUtils";
import { sortCfrSectionsByNumericId } from "@utils/cfrSectionSort";
import { facilityComplianceService, getAccreditationProgramStandardCode, getAccreditationProgramSubject, type StateRequirementsResponse, type AccreditationsResponse, type CFRRegulationSection } from "@services/facilityCompliance";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import AccreditationCalendarView, { AccreditationSectionsView } from "@components/Spaces/AccreditationCalendarView";
import ComplianceRequirementsSearchBar from "@components/Spaces/complianceSearch/ComplianceRequirementsSearchBar";
import ComplianceSearchResults from "@components/Spaces/complianceSearch/ComplianceSearchResults";
import {
  buildComplianceSearchIndex,
  filterComplianceSearch,
  isStateLevelRequirement,
  mergeSearchResults,
  regulationTextHitToSearchItem,
  type ComplianceSearchItem,
} from "@utils/complianceRequirementsSearch";
import { formatFederalRequirementsHeaderCount } from "@utils/federalRequirementsHeader";

const CFR_PAGE_SIZE = 50;

// Styled Components
const ContentContainer = styled(Box)({
  backgroundColor: "#fdffff",
  minHeight: "98vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  position: "relative",
  overflow: "auto",
  width: "100%",
  boxSizing: "border-box",
});

const ContentWrapper = styled(Box)({
  padding: "24px 80px",
  display: "flex",
  flexDirection: "column",
  gap: "24px",
});

const MainContent = styled(Box)({
  width: "100%",
  maxWidth: "1092px",
  height: "auto",
  display: "flex",
  flexDirection: "column",
  //   gap: "8px",
  //   marginTop: "10px",
  alignItems: "top",
});
 

const Breadcrumbs = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  marginTop: "5px",
  marginBottom: "16px",
});

const BreadcrumbButton = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 8px",
  borderRadius: "4px",
  cursor: "pointer",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
}));

const BreadcrumbLink = styled(Typography)<{ isActive?: boolean }>(({ isActive }) => ({
  fontFamily: "Inter, sans-serif",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  letterSpacing: "0.03px",
  color: isActive ? "#086e86" : "#262626",
  whiteSpace: "nowrap",
  cursor: "pointer",
}));

const BreadcrumbDivider = styled(Box)({
  display: "flex",
  alignItems: "center",
  width: "12px",
  height: "12px",
  overflow: "hidden",
});

const HeaderSection = styled(Box)({
  borderBottom: "1px solid #35d7e4",
  paddingBottom: "8px",
  marginBottom: "8px",
  width: "100%",
});

const HeaderTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "8px",
});

const Title = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: "1.5",
  letterSpacing: "-0.4px",
  color: "#05495a",
});
const Title2 = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "16px",
  letterSpacing: "0.3px",
  color: "#086E86", 
  padding: "10px",
});

// Removed unused SectionLabelContainer

const SectionIcon = styled(Box)({
  width: "20px",
  height: "20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

// Removed unused SectionLabel

// Templates/Regulations List Styled Components
// Removed unused RegulationsContainer

// Removed unused RegulationsList

// Removed unused RegulationCard

// Removed unused RegulationName

// Removed unused ChevronIcon

// Removed unused ScrollbarContainer



// Removed unused LoadingContainer

// Removed unused ErrorText

// Compliance Requirements Styled Components
const StateDropdownContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "16px",
  padding: "10px 16px",
  backgroundColor: "#F3FDFF",
  borderRadius: "12px",
  marginBottom: "8px",
  maxWidth: "820px",
  alignSelf: "flex-start",
  border: "1px solid #CCF5FF",
});

const StateContextLabel = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 400,
  fontSize: "14px",
  color: "#4f4f4f",
  whiteSpace: "nowrap",
  flexShrink: 0,
});

const StateInlineLabel = styled("span")({
  fontWeight: 500,
  color: "#262626",
  marginRight: "4px",
});

const StateSelect = styled(Select<string>)({
  width: "600px",
  backgroundColor: "#fdffff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #99d4e1",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #0a829d",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #0a829d",
  },
  "& .MuiSelect-select": {
    padding: "8px",
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "14px",
    color: "#262626",
  },
});

// Removed unused PageHeader

// Removed unused PageTitle

const TabsContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "flex-start",
  width: "100%",
  marginTop: "16px",
  gap: "0px",
});

const TabButton = styled(Box)<{ isActive?: boolean }>(({ isActive }) => ({
  padding: "8px 12px",
  borderBottom: isActive ? "1px solid #065b70" : "none",
  backgroundColor: "transparent",
  cursor: "pointer",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "15px",
  fontWeight: 500,
  color: isActive ? "#065b70" : "#737373",
  lineHeight: 1.5,
  whiteSpace: "nowrap",
  transition: "color 0.2s ease",
  "&:hover": {
    color: "#065b70",
  },
}));

const EmptyStateContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "300px",
  textAlign: "center",
});

const EmptyStateText = styled(Typography)({
	color: "#737373",
	textAlign: "center",
	fontFamily: "Rethink Sans, sans-serif",
	fontSize: "18px",
	fontStyle: "normal",
	fontWeight: 400,
	lineHeight: "24px",
});

const ComplianceSectionHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginTop: "18px",
  marginBottom: "18px",
});

const ComplianceSectionTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "16px",
  color: "#065b70",
});

const CountText = styled("span")({
  marginLeft: 6,
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  color: "#065b70",
  fontVariantNumeric: "tabular-nums",
  fontFeatureSettings: '"calt" 0, "liga" 0',
});

const StyledAccordion = styled(Accordion)({
	marginBottom: "8px",
	borderRadius: "8px !important",
	border: "1px solid #e0e0e0",
	boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
	"&:before": {
		display: "none",
	},
	"& .accordion-title": {
		color: "#262626",
		transition: "color 0.3s ease",
	},
	"&.Mui-expanded .accordion-title": {
		color: "#065B70",
	},
	"& .MuiAccordionSummary-root": {
		alignItems: "center",
	},
	"& .MuiAccordionSummary-content": {
		alignItems: "center",
		flexWrap: "nowrap",
	},
	"& .MuiAccordionDetails-root": {
		paddingTop: "0px",
	},
	"&.Mui-expanded": {
		margin: "0 0 8px 0",
		borderColor: "#9EE5F4 !important",
		boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15) !important",
	},
});

const AccordionHeader = styled(AccordionSummary)({
	padding: "8px",
	minHeight: "48px",
	border: "1px solid #9EE5F4",
	borderRadius: "8px",
	"&.Mui-expanded": {
		minHeight: "48px",
		borderColor: "#9EE5F4",
	},
	"& .MuiAccordionSummary-content": {
		alignItems: "center",
		margin: 0,
	},
	"& .MuiAccordionSummary-content.Mui-expanded": {
		margin: 0,
	},
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: "4px",
  fontFamily: "Rethink Sans, sans-serif",
});

const TableContainer = styled(Box)({
  marginTop: "12px",
});

const Table = styled(Box)({
  backgroundColor: "white",
  border: "1px solid #cfe6ed",
  borderRadius: "8px",
  overflow: "hidden",
});

const TableHeader = styled(Box)({
  display: "grid",
  gridTemplateColumns: "120px 1fr 200px 120px 200px",
  gap: "8px",
  padding: "12px 16px",
  backgroundColor: "#0a829d",
  color: "white",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  fontWeight: 600,
});

const TableRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "120px 1fr 200px 120px 200px",
  gap: "8px",
  padding: "12px 16px",
  borderBottom: "1px solid #e5f5f8",
  alignItems: "center",
  "&:last-child": {
    borderBottom: "none",
  },
});

// Regulatory table variant (6 columns) with narrower References column
const TableHeaderRegulatory = styled(TableHeader)({
  // Priority | Description | Jurisdiction | Regulatory Body | Category | References
  gridTemplateColumns: "0.85fr 1.9fr 1fr 1.35fr 1fr 0.8fr",
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
});

const TableRowRegulatory = styled(TableRow)({
  gridTemplateColumns: "0.85fr 1.9fr 1fr 1.35fr 1fr 0.8fr",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  "& > div": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: "48px",
  },
});

// State facility requirements variant (7 columns) with narrower References column
const TableHeaderRegulatoryState = styled(TableHeader)({
  // Facility Type | License/Program | CON | Construction | Medicare | Medicaid | References
  gridTemplateColumns: "1fr 1.4fr 1fr 1fr 1.35fr 1.35fr 0.8fr",
  whiteSpace: "normal",
  overflow: "visible",
  textOverflow: "clip",
});

const TableRowRegulatoryState = styled(TableRow)({
  gridTemplateColumns: "1fr 1.4fr 1fr 1fr 1.35fr 1.35fr 0.8fr",
  wordWrap: "break-word",
  overflowWrap: "break-word",
  "& > div": {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "flex-start",
    justifyContent: "center",
    minHeight: "48px",
  },
});

// Operational documents table variant
const TableHeaderOperational = styled(TableHeader)({
  gridTemplateColumns: "120px 140px 180px 140px 140px 140px 140px 200px",
});

const TableRowOperational = styled(TableRow)({
  gridTemplateColumns: "120px 140px 180px 140px 140px 140px 140px 200px",
});

const TableCell = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  color: "#262626",
  wordBreak: "break-word",
});

const PriorityChip = styled(Chip)<{ priority: string }>(({ priority }) => {
  const colors: Record<string, { bg: string; text: string; border: string }> = {
    High: { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" },
    Medium: { bg: "#fffbeb", text: "#92400e", border: "#fed7aa" },
    Low: { bg: "#f0f9ff", text: "#1e40af", border: "#bfdbfe" },
  };

  const color = colors[priority] || colors.Medium;

  return {
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
    fontFamily: "Rethink Sans, sans-serif",
    fontSize: "12px",
    fontWeight: 500,
    height: "24px",
  };
});

const CategoryChip = styled(Chip)({
  backgroundColor: "#e6fafc",
  color: "#086e86",
  border: "1px solid #5bbfd6",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  height: "24px",
});

// Wrapped pill that supports multi-line content
const WrappedPill = styled(Box)({
  display: "inline-block",
  maxWidth: "100%",
  backgroundColor: "#e6fafc",
  color: "#086e86",
  border: "1px solid #5bbfd6",
  borderRadius: "12px",
  padding: "4px 8px",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: 1.3,
  whiteSpace: "normal",
  wordBreak: "break-word",
});

const ActionButton = styled(Button)({
  minWidth: "auto",
  padding: "4px 12px",
  fontSize: "12px",
  fontFamily: "Rethink Sans, sans-serif",
  textTransform: "none",
});

const ReferenceButton = styled(ActionButton)({
  backgroundColor: "#e6fafc",
  color: "#086e86",
  border: "1px solid #5bbfd6",
  "&:hover": {
    backgroundColor: "#d0f4f7",
  },
});

// Interface
interface FacilityInputProps {
  selectedSection?: TransformedSection | null;
  selectedCategory: string;
  onBack?: () => void;
  onSubmit?: (data: FacilityInputData) => void;
}

export interface FacilityInputData {
  state: string;
  county: string;
  city: string;
  facilityName: string;
  question: string;
}

// Removed unused RegulatoryRequirement interface

// Removed unused RequirementItem interface

// Removed unused OperationalDocument interface

// Removed unused DocumentItem interface
 

// Icon Components
const ExpandMoreIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 10L12 15L17 10"
      stroke="#262626"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const DocumentTitleICon = () => (
  <svg
  width="20"
  height="20"
  viewBox="0 0 20 20"
  fill="none"
  xmlns="http://www.w3.org/2000/svg"
  >
  <path d="M1.5003 0.5C1.22416 0.5 1.0003 0.723858 1.0003 1C1.0003 1.27614 1.22416 1.5 1.5003 1.5H2.2503L0.0772234 6.71538C0.0278225 6.83395 -0.00317729 6.97168 0.00265526 7.11978C0.0657241 8.7213 1.38356 10 3.0003 10C4.61704 10 5.93488 8.7213 5.99795 7.11978C6.00378 6.97168 5.97278 6.83395 5.92338 6.71538L3.7503 1.5H7.5003V11.5H4.0003C2.89573 11.5 2.0003 12.3954 2.0003 13.5C2.0003 14.6046 2.89573 15.5 4.0003 15.5H12.0003C13.1049 15.5 14.0003 14.6046 14.0003 13.5C14.0003 12.3954 13.1049 11.5 12.0003 11.5H8.5003V1.5H12.2503L10.0772 6.71538C10.0278 6.83395 9.99682 6.97168 10.0027 7.11978C10.0657 8.7213 11.3836 10 13.0003 10C14.617 10 15.9349 8.7213 15.9979 7.11978C16.0038 6.97168 15.9728 6.83395 15.9234 6.71538L13.7503 1.5H14.5003C14.7764 1.5 15.0003 1.27614 15.0003 1C15.0003 0.723858 14.7764 0.5 14.5003 0.5H1.5003ZM3.0003 9C2.0684 9 1.28522 8.3625 1.06326 7.5H4.93734C4.71538 8.3625 3.9322 9 3.0003 9ZM3.0003 2.3L4.7503 6.5H1.2503L3.0003 2.3ZM4.0003 12.5H12.0003C12.5526 12.5 13.0003 12.9477 13.0003 13.5C13.0003 14.0523 12.5526 14.5 12.0003 14.5H4.0003C3.44802 14.5 3.0003 14.0523 3.0003 13.5C3.0003 12.9477 3.44802 12.5 4.0003 12.5ZM14.7503 6.5H11.2503L13.0003 2.3L14.7503 6.5ZM11.0633 7.5H14.9373C14.7154 8.3625 13.9322 9 13.0003 9C12.0684 9 11.2852 8.3625 11.0633 7.5Z" fill="#065B70"/>
</svg>
);
const DocumentIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 2C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V16C4 16.5304 4.21071 17.0391 4.58579 17.4142C4.96086 17.7893 5.46957 18 6 18H14C14.5304 18 15.0391 17.7893 15.4142 17.4142C15.7893 17.0391 16 16.5304 16 16V7L12 3H6Z"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 3V7H16"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RegulatoryIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6C4 4.89543 4.89543 4 6 4H14C15.1046 4 16 4.89543 16 6V14C16 15.1046 15.1046 16 14 16H6C4.89543 16 4 15.1046 4 14V6Z"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 8H12"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <path
      d="M8 12H12"
      stroke="#065b70"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ChevronDividerIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.5 2.5L7.5 6L4.5 9.5"
      stroke="#262626"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function FacilityInputComponent({
  selectedSection,
  selectedCategory,
  onBack,
  onSubmit,
}: FacilityInputProps) {
 
  
  const [selectedState, setSelectedState] = useState("");
  const [activeTab, setActiveTab] = useState<"federal" | "state" | "operational" | "other">("federal");
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({});
  const [states, setStates] = useState<StateData[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string>(getSelectedCountryName());
  const [comprehensiveRequirements, setComprehensiveRequirements] = useState<any>(null);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [stateReqLoading, setStateReqLoading] = useState(false);
  const [stateRequirements, setStateRequirements] = useState<StateRequirementsResponse | null>(null);
  const [accreditationData, setAccreditationData] = useState<AccreditationsResponse | null>(null);
  const [otherTabLoading, setOtherTabLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [textSearchHits, setTextSearchHits] = useState<ComplianceSearchItem[]>([]);
  const [textSearchLoading, setTextSearchLoading] = useState(false);
  const [cfrVisibleCount, setCfrVisibleCount] = useState(CFR_PAGE_SIZE);
  const textSearchAbortRef = useRef<AbortController | null>(null);

  // Removed countries load effect (countries state not used)

  // Listen for country changes from localStorage
  useEffect(() => {
    const checkCountryChange = () => {
      const currentCountry = getSelectedCountryName();
      if (currentCountry !== selectedCountry) {
        console.log("Country changed from", selectedCountry, "to", currentCountry);
        setSelectedCountry(currentCountry);
      }
    };

    // Check on mount
    checkCountryChange();

    // Listen for storage events (changes in other tabs/windows)
    window.addEventListener('storage', checkCountryChange);

    // Poll for changes in current tab (since storage event doesn't fire in same tab)
    const interval = setInterval(checkCountryChange, 1000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkCountryChange);
    };
  }, [selectedCountry]);

  // Load states when component mounts or country changes
  useEffect(() => {
    const loadStates = async () => {
      try {
        setStatesLoading(true);
        // clear previous errors
        const statesData = await getStates(selectedCountry);
        console.log("State List for", selectedCountry, ":", statesData);
        setStates(statesData);
        
        // Reset selected state when country changes
        setSelectedState("");
      } catch (err) {
        console.error("Failed to load states:", err);
        // swallow error: states error not surfaced in UI
      } finally {
        setStatesLoading(false);
      }
    };

    loadStates();
  }, [selectedCountry]);

  // When states load and we have a facility type but no state selected, auto-select state:
  // prefer the state of the user's previously selected facility (session); otherwise first state.
  // This populates the State dropdown and triggers fetches for Federal, State, Operational, and Other tabs.
  useEffect(() => {
    if (states.length === 0 || !selectedSection?.id || selectedState) return;

    const facility = getSelectedFacilityFromSession<{ state?: string }>();
    const facilityState = (facility?.state ?? "").trim();

    if (facilityState) {
      const match =
        states.find(
          (s) =>
            s.name === facilityState ||
            s.state_abbreviation === facilityState ||
            s.state_province === facilityState
        );
      if (match) {
        setSelectedState(match.name);
        return;
      }
      // SSOT-42 / #10166: never default to states[0] (often Alabama) when
      // the session facility has a jurisdiction that did not match the list.
      return;
    }

    setSelectedState(states[0].name);
  }, [states, selectedSection?.id, selectedState]);

  // Fetch comprehensive requirements when state is selected (or after auto-select on load)
  useEffect(() => {
    const fetchComprehensiveRequirements = async () => {
      if (!selectedState) {
        setComprehensiveRequirements(null);
        return;
      }

      try {
        setRequirementsLoading(true);
        const stateData = states.find(s => s.name === selectedState);
        if (stateData) {
          const requirements = await facilityComplianceService.getComprehensiveRequirements({
            facilityTypeSlug: selectedSection?.id || "federally_qualified_health_center",
            countryCode: getSelectedCountryCode(),
            stateProvince: stateData.state_abbreviation
          });
          setComprehensiveRequirements(requirements);
        }
      } catch (err) {
        console.error("Failed to fetch comprehensive requirements:", err);
      } finally {
        setRequirementsLoading(false);
      }
    };

    fetchComprehensiveRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, states]);

  // Fetch state-level facility requirements when state changes (new API)
  useEffect(() => {
    const fetchStateRequirements = async () => {
      if (!selectedState) {
        setStateRequirements(null);
        return;
      }

      try {
        setStateReqLoading(true);
        const stateData = states.find((s) => s.name === selectedState);
        if (stateData) {
          const res = await facilityComplianceService.getStateRequirements({
            facilityTypeSlug: selectedSection?.id || "acute_care_hospital",
            countryCode: getSelectedCountryCode(),
            stateProvince: stateData.state_abbreviation,
          });
          setStateRequirements(res);
        }
      } catch (err) {
        console.error("Failed to fetch state requirements:", err);
        setStateRequirements(null);
      } finally {
        setStateReqLoading(false);
      }
    };

    fetchStateRequirements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, states]);

  // Fetch accreditations (e.g. NIAHO DNV) from dedicated endpoint — no facility-compliance call
  useEffect(() => {
    const fetchAccreditations = async () => {
      if (!selectedState || !selectedSection?.id) {
        setAccreditationData(null);
        return;
      }
      try {
        setOtherTabLoading(true);
        const stateData = states.find((s) => s.name === selectedState);
        const data = await facilityComplianceService.getAccreditations({
          facilityType: selectedSection.id,
          countryCode: getSelectedCountryCode(),
          vertical: "healthcare",
          stateProvince: stateData?.state_abbreviation,
        });
        setAccreditationData(data);
      } catch (err) {
        console.error("Failed to fetch accreditations:", err);
        setAccreditationData(null);
      } finally {
        setOtherTabLoading(false);
      }
    };
    fetchAccreditations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, selectedSection?.id]);

  // Removed legacy regulations fetch (not used in FacilityInput)

  const categoryName = sessionStorage.getItem("CategoryName");
  
  
  const handleStateChange = (event: SelectChangeEvent<string>) => {
    setSelectedState(event.target.value);
  };

  const handleAccordionChange = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const regulatoryReqs = comprehensiveRequirements?.regulatory_requirements ?? [];
  const federalRegulatoryRequirements = regulatoryReqs.filter((req: any) => !isStateLevelRequirement(req));
  const stateLevelRegulatoryRequirements = regulatoryReqs.filter((req: any) => isStateLevelRequirement(req));

  useEffect(() => {
    setCfrVisibleCount(CFR_PAGE_SIZE);
  }, [comprehensiveRequirements?.cfr_regulation_sections?.length, selectedState]);

  const cfrSectionsRaw = comprehensiveRequirements?.cfr_regulation_sections;
  const sortedCfrSections = useMemo(
    () =>
      sortCfrSectionsByNumericId<CFRRegulationSection>(cfrSectionsRaw ?? []),
    [cfrSectionsRaw],
  );
  const visibleCfrSections = sortedCfrSections.slice(0, cfrVisibleCount);
  const federalHeaderCount = formatFederalRequirementsHeaderCount(
    federalRegulatoryRequirements,
    Number(
      comprehensiveRequirements?.total_cfr_sections ?? sortedCfrSections.length ?? 0,
    ),
  );

  const otherRequirements = accreditationData ?? undefined;
  const hasOtherRequirements =
    otherRequirements && Object.keys(otherRequirements).filter((k) => otherRequirements[k]).length > 0;

  const searchIndex = useMemo(
    () =>
      buildComplianceSearchIndex({
        comprehensiveRequirements,
        stateRequirements,
        accreditationData,
        isStateLevelRequirement,
      }),
    [comprehensiveRequirements, stateRequirements, accreditationData],
  );

  const metadataSearchResults = useMemo(
    () => filterComplianceSearch(searchIndex, searchQuery),
    [searchIndex, searchQuery],
  );

  const searchResults = useMemo(
    () => mergeSearchResults(metadataSearchResults, textSearchHits),
    [metadataSearchResults, textSearchHits],
  );

  const isSearchActive = searchQuery.trim().length > 0;

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2 || !selectedSection?.id) {
      setTextSearchHits([]);
      setTextSearchLoading(false);
      textSearchAbortRef.current?.abort();
      return;
    }

    const timer = window.setTimeout(() => {
      textSearchAbortRef.current?.abort();
      const controller = new AbortController();
      textSearchAbortRef.current = controller;
      setTextSearchLoading(true);

      const stateData = states.find((s) => s.name === selectedState);

      facilityComplianceService
        .searchRegulationText({
          facilityTypeSlug: selectedSection.id,
          countryCode: getSelectedCountryCode(),
          stateProvince: stateData?.state_abbreviation,
          query: trimmed,
          signal: controller.signal,
        })
        .then((response) => {
          if (controller.signal.aborted) return;
          setTextSearchHits(
            (response.results ?? []).map((hit, idx) => regulationTextHitToSearchItem(hit, idx)),
          );
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.error("Regulation text search failed:", err);
          setTextSearchHits([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setTextSearchLoading(false);
          }
        });
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [searchQuery, selectedSection?.id, selectedState, states]);

  const handleSearchResultClick = useCallback((item: ComplianceSearchItem) => {
    if (item.sourceType === "federal_register" && item.sectionUrl) {
      window.open(item.sectionUrl, "_blank", "noopener,noreferrer");
      return;
    }
    setActiveTab(item.tab);
    setExpandedSections((prev) => ({ ...prev, [item.expandKey]: true }));
    setSearchQuery("");
    setTextSearchHits([]);
  }, []);

  return (
    <ContentContainer>
      <ContentWrapper>
        <MainContent>
          {/* Breadcrumbs */}
          <Breadcrumbs>
          
            <BreadcrumbButton onClick={onBack}>
              <BreadcrumbLink isActive={false}>Regulations Library</BreadcrumbLink>
            </BreadcrumbButton>
            <BreadcrumbDivider>
              <ChevronDividerIcon />
            </BreadcrumbDivider>
            <BreadcrumbButton onClick={onBack}>
              <BreadcrumbLink isActive={false}>Facilities</BreadcrumbLink>
            </BreadcrumbButton>
            <BreadcrumbDivider>
              <ChevronDividerIcon />
            </BreadcrumbDivider>
            <BreadcrumbButton onClick={onBack}>
              <BreadcrumbLink isActive={false}>{categoryName || "Hospitals & Inpatient Facilities"}</BreadcrumbLink>
            </BreadcrumbButton>
            <BreadcrumbDivider>
              <ChevronDividerIcon />
            </BreadcrumbDivider>
            <BreadcrumbButton>
              <BreadcrumbLink isActive={true}>
                {selectedSection?.title || " "}
            </BreadcrumbLink>
            </BreadcrumbButton>
          </Breadcrumbs>

          {/* Header */}
          <Title2>
            {selectedSection?.title || ""}
          </Title2>    
          <HeaderSection>
            <HeaderTitle>
              <SectionIcon>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3 6.25C3 4.45507 4.45507 3 6.25 3H17.75C19.5449 3 21 4.45507 21 6.25V17.75C21 19.5449 19.5449 21 17.75 21H6.25C4.45507 21 3 19.5449 3 17.75V6.25ZM13.4905 9.49994L8.22232 14.7171C7.928 15.0086 7.92569 15.4834 8.21715 15.7777C8.50862 16.0721 8.98349 16.0744 9.2778 15.7829L14.5 10.6113V15.2499C14.5 15.6642 14.8358 15.9999 15.25 15.9999C15.6642 15.9999 16 15.6642 16 15.2499V8.74994C16 8.33573 15.6642 7.99994 15.25 7.99994H8.75C8.33579 7.99994 8 8.33573 8 8.74994C8 9.16415 8.33579 9.49994 8.75 9.49994H13.4905Z"
                    fill="#05495A"
                  />
                </svg>
              </SectionIcon>
              <Title>
                Facility Compliance Requirements
              </Title>
            </HeaderTitle>
          </HeaderSection>

          {/* State context + dropdown */}
          <StateDropdownContainer>
            <StateContextLabel>
              <StateInlineLabel>Showing requirements for</StateInlineLabel>
            </StateContextLabel>
            <StateSelect
              value={selectedState}
              onChange={handleStateChange}
              displayEmpty
              disabled={statesLoading}
            >
              <MenuItem value="" disabled>
                <span style={{ color: "#a3a3a3" }}>
                  {statesLoading ? "Loading states..." : "Select State"}
                </span>
              </MenuItem>
              {states.map((state) => (
                <MenuItem key={state.id} value={state.name}>
                  {state.name}
                </MenuItem>
              ))}
            </StateSelect>
          </StateDropdownContainer>

          {selectedState ? (
            <ComplianceRequirementsSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              loading={textSearchLoading}
            />
          ) : null}

          {/* Tabs */}
          <TabsContainer>
            <TabButton
              isActive={activeTab === "federal"}
              onClick={() => setActiveTab("federal")}
            >
              Federal
            </TabButton>
            <TabButton
              isActive={activeTab === "state"}
              onClick={() => setActiveTab("state")}
            >
              State
            </TabButton>
            <TabButton
              isActive={activeTab === "operational"}
              onClick={() => setActiveTab("operational")}
            >
              Operational Documents
            </TabButton>
            <TabButton
              isActive={activeTab === "other"}
              onClick={() => setActiveTab("other")}
            >
              Accreditations
            </TabButton>
          </TabsContainer>

          {/* Content */}
          {statesLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, minHeight: "30vh" }}>
              <CircularProgress size={40} sx={{ color: "#0a829d" }} />
              <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}>
                Loading states...
              </Typography>
            </Box>
          ) : !selectedState ? (
            <EmptyStateContainer>
              <EmptyStateText>
                Select State to view facility compliance requirements
              </EmptyStateText>
            </EmptyStateContainer>
          ) : (
            <Box>
              {isSearchActive ? (
                <ComplianceSearchResults
                  query={searchQuery}
                  results={searchResults}
                  onResultClick={handleSearchResultClick}
                  textSearchLoading={textSearchLoading}
                />
              ) : activeTab === "operational" ? (
                // Operational Documents Tab
                <>
                  <ComplianceSectionHeader>
                    <DocumentTitleICon />
                    <ComplianceSectionTitle>
                      Operational Documents
                      <CountText>
                        (
                        {(Number(
                          comprehensiveRequirements?.total_operational_documents ??
                            comprehensiveRequirements?.operational_documents?.length ??
                            0
                        ))}
                        )
                      </CountText>
                    </ComplianceSectionTitle>
                  </ComplianceSectionHeader>

                  {requirementsLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: "20px" }}>
                      <CircularProgress size={40} sx={{ color: "#0a829d" }} />
                      <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}>
                        Loading operational documents...
                      </Typography>
                    </Box>
                  ) : comprehensiveRequirements?.operational_documents && comprehensiveRequirements.operational_documents.length > 0 ? (
                    comprehensiveRequirements.operational_documents.map((doc: any) => (
                      <StyledAccordion
                        key={doc.id}
                        expanded={expandedSections[doc.id] || false}
                        onChange={() => handleAccordionChange(doc.id)}
                        sx={{
                          "&.Mui-expanded": {
                            borderColor: "#99d4e1",
                            boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                          },
                        }}
                      >
                        <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                          <AccordionTitle className="accordion-title">
                            <DocumentIcon />
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 500,
                                fontSize: "16px",
                                color: "#262626",
                              }}
                            >
                              {doc.document_name}
                            </Typography>
                          </AccordionTitle>
                        </AccordionHeader>
                        <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                          <TableContainer>
                            <Table>
                              <TableHeaderOperational>
                                <div>Document Code</div>
                                 <div>Document Name</div>
                                <div>Description</div>
                                <div>Compliance Type</div>
                                <div>Jurisdiction Level</div>
                                <div>As of Date</div>
                                <div>References</div>
                              </TableHeaderOperational>
                              <TableRowOperational>
                                <TableCell>{doc.document_code}</TableCell>
                                <TableCell>{doc.document_name}</TableCell>
                                <TableCell>{doc.notes}</TableCell>
                                <TableCell>{doc.compliance_type}</TableCell>
                                <TableCell>
                                  <CategoryChip label={doc.jurisdiction_level} />
                                </TableCell>
                                <TableCell>{doc.as_of_date}</TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {doc.source_url && (
                                      <ReferenceButton size="small" onClick={() => window.open(doc.source_url, "_blank", "noopener,noreferrer")}>
                                        Source 
                                      </ReferenceButton>
                                    )}
                                    {doc.document_url && (
                                      <ReferenceButton size="small" onClick={() => window.open(doc.document_url, "_blank", "noopener,noreferrer")}>
                                        Document 
                                      </ReferenceButton>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRowOperational>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </StyledAccordion>
                    ))
                  ) : (
                    <Box sx={{ padding: "20px", textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans, sans-serif",
                          fontSize: "16px",
                          color: "#737373",
                        }}
                      >
                        Select a state to view operational documents
                      </Typography>
                    </Box>
                  )}
                </>
              ) : activeTab === "federal" ? (
                // Regulatory Requirements Tab (Federal)
                <>
                  <ComplianceSectionHeader>
                    <RegulatoryIcon />
                    <ComplianceSectionTitle>
                      Regulatory Requirements
                      <CountText>({federalHeaderCount})</CountText>
                    </ComplianceSectionTitle>
                  </ComplianceSectionHeader>
                  <Typography sx={{ fontSize: "14px", color: "#737373", marginBottom: "14px", marginTop: "-10px" }}>
                    Federal-level only. State-level items (e.g. Staff Credentialing / Licensure) appear under the State tab.
                  </Typography>

                  {requirementsLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: "20px" }}>
                      <CircularProgress size={40} sx={{ color: "#0a829d" }} />
                      <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}>
                        Loading regulatory requirements...
                      </Typography>
                    </Box>
                  ) : federalRegulatoryRequirements.length > 0 ? (
                    <>
                      {federalRegulatoryRequirements.map((req: any) => (
                        <StyledAccordion
                          key={req.id}
                          expanded={expandedSections[req.id] || false}
                          onChange={() => handleAccordionChange(req.id)}
                          sx={{
                            "&.Mui-expanded": {
                              borderColor: "#99d4e1",
                              boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                            },
                          }}
                        >
                          <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                            <AccordionTitle className="accordion-title">
                              <DocumentIcon />
                              <Typography
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "16px",
                                  color: "#262626",
                                }}
                              >
                                {req.title}
                              </Typography>
                            </AccordionTitle>
                          </AccordionHeader>
                          <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                            <TableContainer>
                              <Table>
                                <TableHeaderRegulatory>
                                  <div>Priority</div>
                                  <div>Description</div>
                                  <div>Jurisdiction</div>
                                  <div>Regulatory Body</div>
                                  <div>Category</div>
                                  <div>References</div>
                                </TableHeaderRegulatory>
                                <TableRowRegulatory>
                                  <TableCell>
                                    <PriorityChip
                                      label={req.priority_level === 1 ? "High" : req.priority_level === 2 ? "Medium" : "Low"}
                                      priority={req.priority_level === 1 ? "High" : req.priority_level === 2 ? "Medium" : "Low"}
                                    />
                                  </TableCell>
                                  <TableCell>{req.description}</TableCell>
                                  <TableCell>
                                    <WrappedPill>{req.jurisdiction_level ?? "Federal"}</WrappedPill>
                                  </TableCell>
                                  <TableCell>
                                    {req.regulatory_body ?? "—"}
                                  </TableCell>
                                  <TableCell>
                                    <CategoryChip label={req.category} />
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      {req.reference_url && (
                                        <ReferenceButton size="small" onClick={() => window.open(req.reference_url, "_blank", "noopener,noreferrer")}>
                                          Reference
                                        </ReferenceButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRowRegulatory>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </StyledAccordion>
                      ))}

                      {/* CFR Regulation Sections Sub-Area */}
                      {comprehensiveRequirements?.cfr_regulation_sections && comprehensiveRequirements.cfr_regulation_sections.length > 0 && (
                        <>
                          <Box sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 1, 
                            marginTop: "24px", 
                            marginBottom: "12px",
                            paddingLeft: "8px"
                          }}>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 600,
                                fontSize: "14px",
                                color: "#065b70",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              Code of Federal Regulations
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 400,
                                fontSize: "14px",
                                color: "#737373",
                              }}
                            >
                              ({comprehensiveRequirements.cfr_regulation_sections.length})
                            </Typography>
                          </Box>
                          {visibleCfrSections.map((cfr) => (
                            <StyledAccordion
                              key={`cfr-${cfr.regulation_section_id}`}
                              expanded={expandedSections[`cfr-${cfr.regulation_section_id}`] || false}
                              onChange={() => handleAccordionChange(`cfr-${cfr.regulation_section_id}`)}
                              sx={{
                                "&.Mui-expanded": {
                                  borderColor: "#99d4e1",
                                  boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                                },
                              }}
                            >
                              <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                                <AccordionTitle className="accordion-title">
                                  <DocumentIcon />
                                  <Typography
                                    sx={{
                                      fontFamily: "Rethink Sans, sans-serif",
                                      fontWeight: 500,
                                      fontSize: "16px",
                                      color: "#262626",
                                    }}
                                  >
                                    {cfr.ecfr_identifier || cfr.section_identifier} - {cfr.section_name}
                                  </Typography>
                                </AccordionTitle>
                              </AccordionHeader>
                              <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                                <TableContainer>
                                  <Table>
                                    <TableHeaderRegulatory>
                                      <div>Title</div>
                                      <div>Part</div>
                                      <div>Group</div>
                                      <div>Section</div>
                                      <div>Status</div>
                                      <div>References</div>
                                    </TableHeaderRegulatory>
                                    <TableRowRegulatory>
                                      <TableCell>{cfr.cfr_title != null ? `Title ${cfr.cfr_title}` : ""}</TableCell>
                                      <TableCell>{cfr.part_name ?? cfr.part_number ?? ""}</TableCell>
                                      <TableCell>{cfr.group_name ?? cfr.group_short_name ?? ""}</TableCell>
                                      <TableCell>{cfr.ecfr_identifier || cfr.section_identifier}</TableCell>
                                      <TableCell>
                                        <CategoryChip label={cfr.active ? "ACTIVE" : "INACTIVE"} />
                                      </TableCell>
                                      <TableCell>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            gap: 1,
                                            flexWrap: "wrap",
                                            alignItems: "center",
                                          }}
                                        >
                                          {(cfr.section_url ?? (cfr.cfr_title != null && (cfr.ecfr_identifier ?? cfr.section_identifier)
                                            ? `https://www.ecfr.gov/current/title-${cfr.cfr_title}/section-${cfr.ecfr_identifier ?? cfr.section_identifier}`
                                            : null)) && (
                                            <ReferenceButton
                                              size="small"
                                              onClick={() => {
                                                const url = cfr.section_url ?? (cfr.cfr_title != null && (cfr.ecfr_identifier ?? cfr.section_identifier)
                                                  ? `https://www.ecfr.gov/current/title-${cfr.cfr_title}/section-${cfr.ecfr_identifier ?? cfr.section_identifier}`
                                                  : "");
                                                window.open(url, "_blank", "noopener,noreferrer");
                                              }}
                                            >
                                              eCFR ↗
                                            </ReferenceButton>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRowRegulatory>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </StyledAccordion>
                          ))}
                          {cfrVisibleCount < sortedCfrSections.length && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  setCfrVisibleCount((n) =>
                                    Math.min(n + CFR_PAGE_SIZE, sortedCfrSections.length),
                                  )
                                }
                              >
                                Load more CFR sections ({cfrVisibleCount} of {sortedCfrSections.length})
                              </Button>
                            </Box>
                          )}
                        </>
                      )}
                    </>
                  ) : comprehensiveRequirements?.cfr_regulation_sections && comprehensiveRequirements.cfr_regulation_sections.length > 0 ? (
                    // Only CFR sections, no federal regulatory requirements
                    <>
                      <Box sx={{ 
                        display: "flex", 
                        alignItems: "center", 
                        gap: 1, 
                        marginBottom: "12px",
                        paddingLeft: "8px"
                      }}>
                        <Typography
                          sx={{
                            fontFamily: "Rethink Sans, sans-serif",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#065b70",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          Code of Federal Regulations
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Rethink Sans, sans-serif",
                            fontWeight: 400,
                            fontSize: "14px",
                            color: "#737373",
                          }}
                        >
                          ({comprehensiveRequirements.cfr_regulation_sections.length})
                        </Typography>
                      </Box>
                      {visibleCfrSections.map((cfr) => (
                        <StyledAccordion
                          key={`cfr-${cfr.regulation_section_id}`}
                          expanded={expandedSections[`cfr-${cfr.regulation_section_id}`] || false}
                          onChange={() => handleAccordionChange(`cfr-${cfr.regulation_section_id}`)}
                          sx={{
                            "&.Mui-expanded": {
                              borderColor: "#99d4e1",
                              boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                            },
                          }}
                        >
                          <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                            <AccordionTitle className="accordion-title">
                              <DocumentIcon />
                              <Typography
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "16px",
                                  color: "#262626",
                                }}
                              >
                                {cfr.ecfr_identifier || cfr.section_identifier} - {cfr.section_name}
                              </Typography>
                            </AccordionTitle>
                          </AccordionHeader>
                          <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                            <TableContainer>
                              <Table>
                                <TableHeaderRegulatory>
                                  <div>Title</div>
                                  <div>Part</div>
                                  <div>Group</div>
                                  <div>Section</div>
                                  <div>Status</div>
                                  <div>References</div>
                                </TableHeaderRegulatory>
                                <TableRowRegulatory>
                                  <TableCell>{cfr.cfr_title != null ? `Title ${cfr.cfr_title}` : ""}</TableCell>
                                  <TableCell>{cfr.part_name ?? cfr.part_number ?? ""}</TableCell>
                                  <TableCell>{cfr.group_name ?? cfr.group_short_name ?? ""}</TableCell>
                                  <TableCell>{cfr.ecfr_identifier || cfr.section_identifier}</TableCell>
                                  <TableCell>
                                    <CategoryChip label={cfr.active ? "ACTIVE" : "INACTIVE"} />
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        gap: 1,
                                        flexWrap: "wrap",
                                        alignItems: "center",
                                      }}
                                    >
                                      {(cfr.section_url ?? (cfr.cfr_title != null && (cfr.ecfr_identifier ?? cfr.section_identifier)
                                        ? `https://www.ecfr.gov/current/title-${cfr.cfr_title}/section-${cfr.ecfr_identifier ?? cfr.section_identifier}`
                                        : null)) && (
                                        <ReferenceButton
                                          size="small"
                                          onClick={() => {
                                            const url = cfr.section_url ?? (cfr.cfr_title != null && (cfr.ecfr_identifier ?? cfr.section_identifier)
                                              ? `https://www.ecfr.gov/current/title-${cfr.cfr_title}/section-${cfr.ecfr_identifier ?? cfr.section_identifier}`
                                              : "");
                                            window.open(url, "_blank", "noopener,noreferrer");
                                          }}
                                        >
                                          eCFR ↗
                                        </ReferenceButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRowRegulatory>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </StyledAccordion>
                      ))}
                      {cfrVisibleCount < sortedCfrSections.length && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                          <Button
                            variant="outlined"
                            onClick={() =>
                              setCfrVisibleCount((n) =>
                                Math.min(n + CFR_PAGE_SIZE, sortedCfrSections.length),
                              )
                            }
                          >
                            Load more CFR sections ({cfrVisibleCount} of {sortedCfrSections.length})
                          </Button>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ padding: "20px", textAlign: "center" }}>
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans, sans-serif",
                          fontSize: "16px",
                          color: "#737373",
                        }}
                      >
                        No federal-level regulatory requirements are listed for this selection. Try the State tab
                        for provincial or state-issued items, or pick another province or state above.
                      </Typography>
                    </Box>
                  )}
                </>
              ) : activeTab === "state" ? (
                // State Requirements Tab
                <>
                  <ComplianceSectionHeader>
                    <RegulatoryIcon />
                    <ComplianceSectionTitle>
                      State Requirements
                      <CountText>
                        (
                        {Number(stateRequirements?.total_count ?? stateRequirements?.facility_requirements?.length ?? 0) +
                          stateLevelRegulatoryRequirements.length}
                        )
                      </CountText>
                    </ComplianceSectionTitle>
                  </ComplianceSectionHeader>
                  <Typography sx={{ fontSize: "14px", color: "#737373", marginBottom: "14px", marginTop: "-10px" }}>
                    State facility requirements and state-level regulatory items (e.g. Staff Credentialing / Licensure from state agencies).
                  </Typography>

                  {/* State-level regulatory requirements (e.g. credentialing) — issued by state agencies */}
                  {stateLevelRegulatoryRequirements.length > 0 && (
                    <>
                      <Typography
                        sx={{
                          fontFamily: "Rethink Sans, sans-serif",
                          fontWeight: 600,
                          fontSize: "14px",
                          color: "#065b70",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: 1,
                        }}
                      >
                        State-level regulatory requirements
                      </Typography>
                      {stateLevelRegulatoryRequirements.map((req: any) => (
                        <StyledAccordion
                          key={req.id}
                          expanded={expandedSections[req.id] || false}
                          onChange={() => handleAccordionChange(req.id)}
                          sx={{
                            "&.Mui-expanded": {
                              borderColor: "#99d4e1",
                              boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                            },
                          }}
                        >
                          <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                            <AccordionTitle className="accordion-title">
                              <DocumentIcon />
                              <Typography
                                sx={{
                                  fontFamily: "Rethink Sans, sans-serif",
                                  fontWeight: 500,
                                  fontSize: "16px",
                                  color: "#262626",
                                }}
                              >
                                {req.title}
                              </Typography>
                            </AccordionTitle>
                          </AccordionHeader>
                          <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                            <TableContainer>
                              <Table>
                                <TableHeaderRegulatory>
                                  <div>Priority</div>
                                  <div>Description</div>
                                  <div>Jurisdiction</div>
                                  <div>Regulatory Body</div>
                                  <div>Category</div>
                                  <div>References</div>
                                </TableHeaderRegulatory>
                                <TableRowRegulatory>
                                  <TableCell>
                                    <PriorityChip
                                      label={req.priority_level === 1 ? "High" : req.priority_level === 2 ? "Medium" : "Low"}
                                      priority={req.priority_level === 1 ? "High" : req.priority_level === 2 ? "Medium" : "Low"}
                                    />
                                  </TableCell>
                                  <TableCell>{req.description}</TableCell>
                                  <TableCell>
                                    <WrappedPill>{req.jurisdiction_name || req.regulatory_body || "State"}</WrappedPill>
                                  </TableCell>
                                  <TableCell>
                                    {req.regulatory_body ?? "—"}
                                  </TableCell>
                                  <TableCell>
                                    <CategoryChip label={req.category} />
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                      {req.reference_url && (
                                        <ReferenceButton size="small" onClick={() => window.open(req.reference_url, "_blank", "noopener,noreferrer")}>
                                          Reference
                                        </ReferenceButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRowRegulatory>
                              </Table>
                            </TableContainer>
                          </AccordionDetails>
                        </StyledAccordion>
                      ))}
                    </>
                  )}

                  {stateReqLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: "20px" }}>
                      <CircularProgress size={40} sx={{ color: "#0a829d" }} />
                      <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}>
                        Loading state requirements...
                      </Typography>
                    </Box>
                  ) : stateRequirements?.facility_requirements && stateRequirements.facility_requirements.length > 0 ? (
                    stateRequirements.facility_requirements.map((item, idx) => (
                      <StyledAccordion
                        key={`${item.facility_type_name}-${idx}`}
                        expanded={expandedSections[`state-${idx}`] || false}
                        onChange={() => handleAccordionChange(`state-${idx}`)}
                        sx={{
                          "&.Mui-expanded": {
                            borderColor: "#99d4e1",
                            boxShadow: "0 0 0 1px rgba(153, 212, 225, 0.35)",
                          },
                        }}
                      >
                        <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                          <AccordionTitle className="accordion-title">
                            <DocumentIcon />
                            <Typography
                              sx={{
                                fontFamily: "Rethink Sans, sans-serif",
                                fontWeight: 500,
                                fontSize: "16px",
                                color: "#262626",
                              }}
                            >
                              {item.license_or_regulatory_program}
                            </Typography>
                          </AccordionTitle>
                        </AccordionHeader>
                        <AccordionDetails sx={{ padding: "0 12px 12px" }}>
                          <TableContainer>
                            <Table>
                              <TableHeaderRegulatoryState>
                                <div>Facility Type</div>
                                <div>License/Program</div>
                                <div>Certificate of Need</div>
                                <div>Construction Review</div>
                                <div>Medicare Certification</div>
                                <div>Medicaid Enrollment</div>
                                <div>References</div>
                              </TableHeaderRegulatoryState>
                              <TableRowRegulatoryState>
                                <TableCell>{item.facility_type_name}</TableCell>
                                <TableCell>{item.license_or_regulatory_program}</TableCell>
                                <TableCell>
                                  <CategoryChip label={(item.certificate_of_need_required || "").toString().toUpperCase()} />
                                </TableCell>
                                <TableCell>
                                  <CategoryChip label={(item.construction_review_required || "").toString().toUpperCase()} />
                                </TableCell>
                                <TableCell>
                                  <WrappedPill>{(item.medicare_certification_survey_required || "").toString()}</WrappedPill>
                                </TableCell>
                                <TableCell>
                                  <WrappedPill>{(item.medicaid_enrollment_revalidation_required || "").toString()}</WrappedPill>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    {item.legal_code_reference && (
                                      <ReferenceButton size="small" onClick={() => window.open(item.legal_code_reference, "_blank", "noopener,noreferrer")}>
                                        Reference
                                      </ReferenceButton>
                                    )}
                                    {item.application_submission_url && (
                                      <ReferenceButton size="small" onClick={() => window.open(item.application_submission_url, "_blank", "noopener,noreferrer")}>
                                        Apply
                                      </ReferenceButton>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRowRegulatoryState>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </StyledAccordion>
                    ))
                  ) : stateLevelRegulatoryRequirements.length === 0 ? (
                    <Box sx={{ padding: "20px", textAlign: "center" }}>
                      <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: "16px", color: "#737373" }}>
                        No state requirements found for the selected state
                      </Typography>
                    </Box>
                  ) : null}
                </>
              ) : activeTab === "other" ? (
                // Accreditations tab — accordions per program (e.g. NIAHO DNV), each with its own heading
                <>
                  <ComplianceSectionHeader>
                    <RegulatoryIcon />
                    <ComplianceSectionTitle>
                      Accreditations
                      <CountText>
                        ({Object.keys(otherRequirements ?? {}).filter((k) => otherRequirements?.[k]).length})
                      </CountText>
                    </ComplianceSectionTitle>
                  </ComplianceSectionHeader>
                  {otherTabLoading ? (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, padding: "20px" }}>
                      <CircularProgress size={40} sx={{ color: "#0a829d" }} />
                      <Typography sx={{ color: "#737373", fontSize: 16, fontWeight: 500 }}>
                        Loading…
                      </Typography>
                    </Box>
                  ) : hasOtherRequirements ? (
                    <>
                      {Object.entries(otherRequirements!).map(([programKey, programData]) => {
                        if (!programData) return null;
                        const displayName = programData.program ?? programKey.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim();
                        const standardCode = getAccreditationProgramStandardCode(programData);
                        const subject = getAccreditationProgramSubject(programData);
                        const titleWithCode = standardCode
                          ? subject
                            ? `${displayName} — ${standardCode} (${subject})`
                            : `${displayName} — ${standardCode}`
                          : subject
                            ? `${displayName} (${subject})`
                            : displayName;
                        return (
                          <StyledAccordion
                            key={programKey}
                            defaultExpanded={false}
                            sx={{ "&:before": { display: "none" } }}
                          >
                            <AccordionHeader expandIcon={<ExpandMoreIcon />}>
                              <AccordionTitle className="accordion-title">
                                <DocumentIcon />
                                <Typography
                                  sx={{
                                    fontFamily: "Rethink Sans, sans-serif",
                                    fontWeight: 500,
                                    fontSize: "16px",
                                    color: "#262626",
                                  }}
                                >
                                {titleWithCode}
                                {programData.revision && (
                                  <Typography component="span" sx={{ fontSize: "12px", fontWeight: 500, color: "#6b7280", ml: 1 }}>
                                    (Revision {programData.revision}
                                    {programData.effectiveDate ? `, effective ${programData.effectiveDate}` : ""})
                                  </Typography>
                                )}
                                </Typography>
                              </AccordionTitle>
                            </AccordionHeader>
                            <AccordionDetails sx={{ pt: 0 }}>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                {programData.calendarItems && programData.calendarItems.length > 0 && (
                                  <AccreditationCalendarView
                                    calendarItems={programData.calendarItems}
                                    revision={programData.revision}
                                    effectiveDate={programData.effectiveDate}
                                    programLinks={programData.programLinks}
                                  />
                                )}
                                {programData.sections && programData.sections.length > 0 && (
                                  <AccreditationSectionsView
                                    sections={programData.sections}
                                    programLinks={programData.programLinks}
                                  />
                                )}
                              </Box>
                            </AccordionDetails>
                          </StyledAccordion>
                        );
                      })}
                    </>
                  ) : (
                    <EmptyStateContainer>
                      <EmptyStateText>
                        {selectedState
                          ? `No accreditation program content for ${selectedState}`
                          : "Select State to view accreditation program content"}
                      </EmptyStateText>
                    </EmptyStateContainer>
                  )}
                </>
              ) : (
                <EmptyStateContainer>
                  <EmptyStateText>Not Available</EmptyStateText>
                </EmptyStateContainer>
              )}
            </Box>
          )}
        </MainContent>

        {/* Question Input (Fixed at bottom) */}
        {/* <QuestionInputContainer>
          <Box
            sx={{
              width: "4px",
              height: "20px",
              backgroundColor: "#065b70",
              marginRight: "8px",
            }}
          />
          <QuestionInput
            placeholder="Requirement for policy"
            value={formData.question}
            onChange={handleInputChange("question")}
            onKeyPress={handleKeyPress}
            multiline
            maxRows={3}
          />
          <SendButton
            onClick={handleSubmit}
            disabled={!formData.question.trim()}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z"
                fill="currentColor"
              />
            </svg>
          </SendButton>
        </QuestionInputContainer> */}
      </ContentWrapper>
    </ContentContainer>
  );
}
